import { Button, HelpText, Textfield, token } from "@may-i/mash-design-system";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { LOCAL_STORAGE_AUTH_CHECK_KEY } from "~/shared/constants/auth";
import { checkIsLoggedIn } from "~/shared/lib/auth";
import { checkIsSafeRedirect } from "~/shared/lib/validate";
import { getErrorMessage } from "~/shared/lib/api-error";
import { useLogin } from "~/features/auth/hooks/use-login";

type LoginFormValues = {
  email: string;
  password: string;
};

export function LoginPage() {
  const { t } = useTranslation("login");

  const { login } = useLogin();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<LoginFormValues>({ mode: "onBlur" });

  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const emailValue = watch("email");
  const passwordValue = watch("password");
  const isFormFilled = !!emailValue && !!passwordValue;
  const hasEmailError = !!errors.email;

  const isLoggedIn = checkIsLoggedIn();

  if (isLoggedIn) {
    const redirectPath = localStorage.getItem("redirectAfterLogin");
    const lastPath = localStorage.getItem("mst:lastPath");
    const targetPath =
      [location.state?.from, redirectPath, lastPath].find(
        (p) => p && checkIsSafeRedirect(p),
      ) ?? "/";

    return <Navigate to={targetPath} replace />;
  }

  const onSubmit = async (data: LoginFormValues) => {
    setLoginError(null);
    console.log("[Login] baseURL:", import.meta.env.VITE_BASE_API_URL);

    try {
      await login(data);
      window.localStorage.setItem(LOCAL_STORAGE_AUTH_CHECK_KEY, "true");

      // 우선순위: location.state.from > redirectAfterLogin > mst:lastPath > /
      // (위 "이미 로그인됨" 분기와 동일하게 state.from 을 1순위로 본다.
      //  AuthGuard 가 미로그인 진입 시 원래 경로를 state.from 으로 넘김)
      // Note: redirectAfterLogin은 AuthGuard 마운트 시 정리됨
      const redirectPath = localStorage.getItem("redirectAfterLogin");
      const lastPath = localStorage.getItem("mst:lastPath");

      const targetPath = [location.state?.from, redirectPath, lastPath].find(
        (p) => p && checkIsSafeRedirect(p),
      );

      navigate(targetPath ?? "/", { replace: true });
    } catch (error: any) {
      console.error("[Login Error]", {
        message: error?.message,
        code: error?.code,
        status: error?.response?.status,
        data: error?.response?.data,
        requestURL: error?.config?.url,
        baseURL: error?.config?.baseURL,
      });
      const errorMessage = getErrorMessage(
        error,
        t("loginForm.loginError.fallbackMessage"),
      );
      setLoginError(errorMessage);
    }
  };

  return (
    <PageContainer>
      <FormContainer>
        <LogoImage src="/assets/images/mst-logo.svg" alt="mST Logo" />

        <Form onSubmit={handleSubmit(onSubmit)}>
          <FieldGroup>
            <EmailFieldWrapper>
              <Textfield
                type="email"
                size="medium"
                label={t("loginForm.emailTextfield.label")}
                error={hasEmailError || (loginError && isFormFilled) ? true : undefined}
                {...register("email", {
                  required: true,
                  pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                })}
              />
              {hasEmailError && (
                <ErrorHelpText state="error" icon="ic_error" size="medium">
                  {t("loginForm.emailTextfield.invalidFormat")}
                </ErrorHelpText>
              )}
            </EmailFieldWrapper>
            <PasswordFieldWrapper>
              <Textfield
                type={showPassword ? "text" : "password"}
                size="medium"
                label={t("loginForm.passwordTextfield.label")}
                error={loginError && isFormFilled ? true : undefined}
                endIcon={showPassword ? "ic_view" : "ic_view_off"}
                endIconProps={{
                  onClick: () => setShowPassword((prev) => !prev),
                  className: "password-visibility-icon",
                }}
                {...register("password", { required: true })}
              />
              {loginError && (
                <ErrorHelpText state="error" icon="ic_error" size="medium">
                  {t("loginForm.loginError.helpText")}
                </ErrorHelpText>
              )}
            </PasswordFieldWrapper>
          </FieldGroup>

          <ActionGroup>
            <LoginButton
              size="large"
              color="primary"
              disabled={!isFormFilled}
            >
              {t("loginForm.loginButton.textContent")}
            </LoginButton>
            <ContactInquiryRow>
              <ContactInquiryLink
                href="https://mash-board.channel.io/home"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("loginForm.contactInquiry")}
              </ContactInquiryLink>
            </ContactInquiryRow>
          </ActionGroup>
        </Form>
      </FormContainer>
    </PageContainer>
  );
}

const PageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: ${token("colors.gray.white")};
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 48px;
  width: 394px;
`;

const LogoImage = styled.img`
  height: 40px;
  width: auto;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  gap: 24px;
  width: 100%;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  gap: 12px;
  width: 100%;
`;

const EmailFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const PasswordFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  .password-visibility-icon {
    color: ${token("colors.text.disabled")};
    cursor: pointer;
  }
`;

const ActionGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  gap: 8px;
  width: 100%;
`;

const ErrorHelpText = styled(HelpText)`
  display: flex;
  align-items: center;
  gap: 4px;

  & > svg {
    width: 14px;
    min-width: 14px;
    height: 14px;
    min-height: 14px;
  }
`;

const LoginButton = styled(Button)`
  width: 100%;
`;

const ContactInquiryRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  align-self: stretch;
  gap: 10px;
`;

const ContactInquiryLink = styled.a`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.57;
  color: ${token("colors.gray.GR700")};
  cursor: pointer;
  text-decoration: underline;
`;
